import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from '../components/ui/switch';
import { Bell, Zap, Trophy, MessageCircle, Calendar, Flame } from 'lucide-react';
import FrancIcon from './FrancIcon';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({
    score_updates: true,
    leaderboard_changes: true,
    chat_messages: true,
    price_changes: false,
    weekly_summary: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/notifications/preferences').then(res => {
      const p = res.data.preferences || {};
      setPrefs(prev => ({ ...prev, ...p }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const updatePref = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    try {
      await api.post('/notifications/preferences', updated);
      toast.success('Preferences saved');
    } catch (e) {
      toast.error('Failed to save');
      setPrefs(prefs); // revert
    }
  };

  const options = [
    // v5.7.57 — labels reflect the desired notification behaviour:
    // score_updates ships as an HOURLY DIGEST (not per-event spam)
    // and price_changes only fires on SIGNIFICANT moves (≥25%).
    { key: 'score_updates', label: 'Score Summary (Hourly)', desc: 'Hourly digest of points your team earned — no per-event spam', icon: Zap },
    { key: 'leaderboard_changes', label: 'Leaderboard Changes', desc: 'When your rank changes', icon: Trophy },
    { key: 'chat_messages', label: 'Chat Messages', desc: 'New messages in your leagues', icon: MessageCircle },
    { key: 'price_changes', label: 'Significant Price Moves (≥25%)', desc: 'Only when a person\u2019s price moves 25 % or more up or down', icon: FrancIcon },
    { key: 'weekly_summary', label: 'Weekly Summary', desc: 'End-of-week performance recap', icon: Calendar },
    { key: 'trending_alerts', label: 'Trending Now Alerts', desc: 'When someone\u2019s X mentions suddenly spike — buy before the crowd', icon: Flame },
  ];

  return (
    <div data-testid="notification-settings">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Bell className="h-4 w-4" /> Notification Preferences
      </h3>
      <div className="space-y-1">
        {options.map(({ key, label, desc, icon: Icon }) => (
          <div key={key} className="flex items-center justify-between px-3 py-3 rounded-xl border border-border/40 bg-card">
            <div className="flex items-center gap-3">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-[10px] text-muted-foreground">{desc}</p>
              </div>
            </div>
            <Switch
              checked={prefs[key]}
              onCheckedChange={(val) => updatePref(key, val)}
              disabled={loading}
              data-testid={`notif-pref-${key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
